import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'
import { LOCALES, type Locale } from '@cgp/shared'

/**
 * Runtime language switch, reachable from every screen (NFR-I18N-02).
 * The chosen locale persists locally and is also saved to the user profile via
 * PATCH /me/locale so it follows the user across devices.
 */
export function LocaleSwitcher({ onPersist }: { onPersist?: (locale: Locale) => void }) {
  const { i18n, t } = useTranslation('common')
  const current = (i18n.resolvedLanguage ?? 'vi') as Locale

  const change = async (locale: Locale) => {
    if (locale === current) return
    await i18n.changeLanguage(locale)
    document.documentElement.lang = locale
    onPersist?.(locale)
  }

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2.5 py-1.5"
      role="group"
      aria-label={t('locale.label')}
    >
      <Languages className="h-4 w-4 text-slate-500" aria-hidden="true" />
      {LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          onClick={() => void change(locale)}
          aria-pressed={locale === current}
          className={[
            'rounded-full px-2.5 py-1.5 text-xs font-semibold transition',
            locale === current
              ? 'bg-brand-500 text-slate-900'
              : 'text-slate-500 hover:text-slate-800',
          ].join(' ')}
        >
          {locale.toUpperCase()}
        </button>
      ))}
    </div>
  )
}
